import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyImageUrl } from '../lib/propertyImage';
import { Property } from '../types';
import SearchPage from './SearchPage';
import './HomePage.css';

// 物件の highlights（DBのJSON文字列）を配列にパースする
function parseHighlights(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

// 物件カードに表示するスペック（面積→最大人数→ベッドルーム→リビング→トイレ→お風呂/シャワー）
// 値が未設定（null/0）の項目は表示しない。label は i18n キーを返し、JSX 側で翻訳する。
function cardSpecs(p: Property, t: (k: string, o?: any) => string): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  if (p.area_sqm) out.push({ label: t('property.spec.area'), value: `${p.area_sqm}${t('property.spec.areaUnit')}` });
  if (p.max_guests) out.push({ label: t('property.maxGuests'), value: `${p.max_guests}${t('property.spec.guestUnit')}` });
  if (p.bedroom_count) out.push({ label: t('property.spec.bedroom'), value: `${p.bedroom_count}` });
  if (p.living_room_count) out.push({ label: t('property.spec.livingRoom'), value: `${p.living_room_count}` });
  if (p.toilet_count) out.push({ label: t('property.spec.toilet'), value: `${p.toilet_count}` });
  if (p.bath_count) out.push({ label: t('property.spec.bath'), value: `${p.bath_count}` });
  return out;
}

function HomePage() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch('/api/properties');
      if (!response.ok) {
        throw new Error('Failed to fetch properties');
      }
      const data = await response.json();
      setProperties(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="error">{t('common.error')}: {error}</div>;
  }

  return (
    <div className="home-page">
      {/* 検索機能をホーム最上部に配置 */}
      <SearchPage />

      <section className="properties-preview">
        <h2>{t('header.properties')}</h2>
        <div className="properties-grid">
          {properties.slice(0, 3).map((property) => (
            <div key={property.id} className="property-card">
              <div className="property-image">
                <img src={propertyImageUrl(property.id)} alt={property.name} />
              </div>
              <div className="property-info">
                <h3>{property.name}</h3>
                {cardSpecs(property, t).length > 0 && (
                  <dl className="property-card-specs">
                    {cardSpecs(property, t).map((sp, i) => (
                      <div key={i} className="spec-row">
                        <dt>{sp.label}</dt>
                        <dd>{sp.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {parseHighlights(property.highlights).length > 0 && (
                  <ul className="property-highlights">
                    {parseHighlights(property.highlights).map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                )}
                {property.detail_url ? (
                  <a
                    href={property.detail_url}
                    className="btn btn-outline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('home.viewProperties')}
                  </a>
                ) : (
                  <Link to={`/property/${property.id}`} className="btn btn-outline">
                    {t('home.viewProperties')}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default HomePage;
